import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './AzureSpeechTool.module.css';

const POLL_MS = 5000;

function ts() {
  return new Date().toLocaleTimeString('zh-CN', { hour12: false });
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function AzureSpeechTool() {
  /* ── volume ── */
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [selectedVolumeId, setSelectedVolumeId] = useState('');

  /* ── voices ── */
  const [allVoices, setAllVoices] = useState({ en: [], zh: [] });
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [enVoice, setEnVoice] = useState('');
  const [cnVoice, setCnVoice] = useState('');
  const [concurrency, setConcurrency] = useState(10);

  /* ── voice filters ── */
  const [enGender, setEnGender] = useState('');       // '' | 'Male' | 'Female'
  const [enRegion, setEnRegion] = useState('');        // '' | 'US' | 'GB' ...
  const [enMulti, setEnMulti] = useState('');          // '' | 'yes' | 'no'
  const [cnGender, setCnGender] = useState('');
  const [cnRegion, setCnRegion] = useState('');
  const [cnMulti, setCnMulti] = useState('');

  /* ── task ── */
  const [taskId, setTaskId] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [chapters, setChapters] = useState([]);
  const [taskStatus, setTaskStatus] = useState('');
  const [succeeded, setSucceeded] = useState(0);
  const [failed, setFailed] = useState(0);

  /* ── preview ── */
  const [previewText, setPreviewText] = useState('');
  const [previewLang, setPreviewLang] = useState('en'); // 'en' | 'zh'
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewAudioRef = useRef(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState(null);

  /* ── logs ── */
  const [logs, setLogs] = useState([]);
  const [globalError, setGlobalError] = useState('');
  const logEndRef = useRef(null);
  const cancelledRef = useRef(false);

  const addLog = useCallback((text, type = 'info') => {
    setLogs((prev) => [...prev, { time: ts(), text, type }]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  /* ── derived ── */
  const selectedVolume = useMemo(
    () => volumes.find((v) => String(v.id) === String(selectedVolumeId)) || null,
    [volumes, selectedVolumeId],
  );

  const chaptersInVolume = useMemo(() => {
    if (!selectedVolume || !Array.isArray(selectedVolume.chapters)) return [];
    return [...selectedVolume.chapters].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
  }, [selectedVolume]);

  /* ── filtered voices ── */
  const filterVoices = useCallback((voices, gender, region, multi) => {
    return voices.filter((v) => {
      if (gender && v.gender !== gender) return false;
      if (region && v.region !== region) return false;
      if (multi === 'yes' && !v.multilingual) return false;
      if (multi === 'no' && v.multilingual) return false;
      return true;
    });
  }, []);

  const enVoicesFiltered = useMemo(
    () => filterVoices(allVoices.en, enGender, enRegion, enMulti),
    [allVoices.en, enGender, enRegion, enMulti, filterVoices],
  );
  const cnVoicesFiltered = useMemo(
    () => filterVoices(allVoices.zh, cnGender, cnRegion, cnMulti),
    [allVoices.zh, cnGender, cnRegion, cnMulti, filterVoices],
  );

  // 提取可用的地区选项
  const enRegions = useMemo(() => {
    const set = new Set(allVoices.en.map((v) => v.region).filter(Boolean));
    return [...set].sort();
  }, [allVoices.en]);
  const cnRegions = useMemo(() => {
    const set = new Set(allVoices.zh.map((v) => v.region).filter(Boolean));
    return [...set].sort();
  }, [allVoices.zh]);

  // 当筛选结果变化时，自动选中第一个（如果当前选中的不在列表中）
  useEffect(() => {
    if (enVoicesFiltered.length > 0 && !enVoicesFiltered.some((v) => v.shortName === enVoice)) {
      setEnVoice(enVoicesFiltered[0].shortName);
    }
  }, [enVoicesFiltered, enVoice]);

  useEffect(() => {
    if (cnVoicesFiltered.length > 0 && !cnVoicesFiltered.some((v) => v.shortName === cnVoice)) {
      setCnVoice(cnVoicesFiltered[0].shortName);
    }
  }, [cnVoicesFiltered, cnVoice]);

  /* ── load volumes ── */
  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    try {
      const { data } = await api.get('/volumes/', {
        params: { limit: 500, include_chapters: true },
      });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((v) => String(v.id) === String(prev))) return prev;
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (err) {
      addLog(`获取卷列表失败：${err?.response?.data?.detail || err?.message || '未知错误'}`, 'error');
    } finally {
      setVolumesLoading(false);
    }
  }, [addLog]);

  /* ── load voices ── */
  const loadVoices = useCallback(async () => {
    setVoicesLoading(true);
    try {
      const { data } = await api.get('/azure-speech/voices');
      const voices = data?.voices || { en: [], zh: [] };
      setAllVoices(voices);

      // 自动选择默认语音
      if (voices.en?.length > 0 && !enVoice) {
        const jenny = voices.en.find((v) => v.shortName === 'en-US-JennyNeural');
        setEnVoice(jenny ? jenny.shortName : voices.en[0].shortName);
      }
      if (voices.zh?.length > 0 && !cnVoice) {
        const xiaoxiao = voices.zh.find((v) => v.shortName === 'zh-CN-XiaoxiaoNeural');
        setCnVoice(xiaoxiao ? xiaoxiao.shortName : voices.zh[0].shortName);
      }
    } catch (err) {
      addLog(`获取语音列表失败：${err?.response?.data?.detail || err?.message || '未知错误'}`, 'error');
    } finally {
      setVoicesLoading(false);
    }
  }, [addLog, enVoice, cnVoice]);

  useEffect(() => {
    loadVolumes();
    loadVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── start batch ── */
  const handleStart = useCallback(async () => {
    if (!selectedVolumeId || chaptersInVolume.length === 0) {
      setGlobalError('请先选择一个有章节的卷');
      return;
    }
    if (!enVoice || !cnVoice) {
      setGlobalError('请选择英文和中文语音');
      return;
    }

    setGlobalError('');
    setLogs([]);
    setRunning(true);
    setProgress(0);
    setChapters([]);
    setSucceeded(0);
    setFailed(0);
    setTaskStatus('');
    cancelledRef.current = false;

    const volLabel = selectedVolume?.novel?.title
      ? `${selectedVolume.novel.title} — ${selectedVolume.title}`
      : selectedVolume?.title || selectedVolumeId;

    addLog(`===== Azure 语音生成启动 =====`);
    addLog(`卷: ${volLabel} | ${chaptersInVolume.length} 章`);
    addLog(`英文: ${enVoice} | 中文: ${cnVoice} | 并发: ${concurrency}`);

    try {
      const { data } = await api.post('/azure-speech/batch', {
        volume_id: Number(selectedVolumeId),
        en_voice: enVoice,
        cn_voice: cnVoice,
        concurrency,
        skip_existing: true,
      });

      if (!data?.task_id) {
        throw new Error('未能启动任务');
      }

      const tid = data.task_id;
      setTaskId(tid);
      addLog(`任务 ${tid.slice(0, 8)} 已启动，轮询中…`);

      // 轮询
      let consecutiveErrors = 0;
      while (true) {
        if (cancelledRef.current) break;
        await sleep(POLL_MS);

        try {
          const { data: s } = await api.get(`/azure-speech/batch/status/${tid}`);
          consecutiveErrors = 0;

          setProgress(s.progress || 0);
          setSucceeded(s.succeeded || 0);
          setFailed(s.failed || 0);
          setTaskStatus(s.status);

          if (Array.isArray(s.chapters)) {
            setChapters(s.chapters);
          }

          if (s.status === 'completed' || s.status === 'completed_with_errors' || s.status === 'cancelled') {
            addLog(
              `任务完成: 成功 ${s.succeeded}/${s.total_chapters}，失败 ${s.failed}`,
              s.failed > 0 ? 'error' : 'success',
            );
            break;
          }
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            addLog(`轮询连续失败 5 次，停止`, 'error');
            break;
          }
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || '未知错误';
      addLog(`启动失败: ${msg}`, 'error');
      setGlobalError(msg);
    }

    setRunning(false);
    addLog(`===== 结束 =====`, 'success');
  }, [
    selectedVolumeId,
    selectedVolume,
    chaptersInVolume,
    enVoice,
    cnVoice,
    concurrency,
    addLog,
  ]);

  /* ── cancel ── */
  const handleStop = useCallback(async () => {
    cancelledRef.current = true;
    if (taskId) {
      try {
        await api.post(`/azure-speech/batch/cancel/${taskId}`);
        addLog('已发送取消请求', 'error');
      } catch {
        addLog('取消请求失败', 'error');
      }
    }
  }, [taskId, addLog]);

  /* ── preview ── */
  const handlePreview = useCallback(async () => {
    const voice = previewLang === 'en' ? enVoice : cnVoice;
    if (!voice) {
      setGlobalError('请先选择语音');
      return;
    }
    if (!previewText.trim()) {
      setGlobalError('请输入试听文本');
      return;
    }
    setGlobalError('');
    setPreviewLoading(true);

    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewFilename(null);

    try {
      const resp = await api.post('/azure-speech/preview', {
        text: previewText.trim(),
        voice,
      }, { responseType: 'blob' });

      const blob = new Blob([resp.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      setPreviewBlobUrl(url);

      const filename = resp.headers?.['x-preview-filename'] || null;
      setPreviewFilename(filename);

      const audio = new Audio(url);
      previewAudioRef.current = audio;
      audio.onended = () => {
        previewAudioRef.current = null;
      };
      audio.play();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || '试听失败';
      setGlobalError(typeof detail === 'string' ? detail : '试听失败');
    } finally {
      setPreviewLoading(false);
    }
  }, [previewText, previewLang, enVoice, cnVoice, previewBlobUrl]);

  const handleDownloadPreview = useCallback(() => {
    if (!previewBlobUrl) return;
    const a = document.createElement('a');
    a.href = previewBlobUrl;
    a.download = previewFilename || 'preview.mp3';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [previewBlobUrl, previewFilename]);

  /* ── expanded chapter (to show failed sentences) ── */
  const [expandedChapterId, setExpandedChapterId] = useState(null);

  /* ── status tag ── */
  const renderTag = (status) => {
    if (!status || status === 'pending') return <span className={styles.tagPending}>等待</span>;
    if (status === 'done') return <span className={styles.tagDone}>✓</span>;
    if (status === 'done_with_errors') return <span className={styles.tagWarn}>⚠</span>;
    if (status === 'failed_sentences') return <span className={styles.tagFailed}>句失败</span>;
    if (status === 'skipped') return <span className={styles.tagSkipped}>跳过</span>;
    if (status.startsWith('failed')) return <span className={styles.tagFailed}>✕</span>;
    return <span className={styles.tagRunning}>…</span>;
  };

  /* ── chapter item class ── */
  const chapterItemClass = (ch, idx) => {
    const base = styles.chapterItem;
    if (!ch) return base;
    const enOk = ch.en === 'done' || ch.en === 'skipped';
    const cnOk = ch.cn === 'done' || ch.cn === 'skipped';
    const hasSentenceFail = ch.en === 'failed_sentences' || ch.cn === 'failed_sentences';
    if (enOk && cnOk) return `${base} ${styles.chapterItemDone}`;
    if (hasSentenceFail) return `${base} ${styles.chapterItemWarn}`;
    if (ch.en?.startsWith('failed') || ch.cn?.startsWith('failed')) return `${base} ${styles.chapterItemFailed}`;
    return `${base} ${styles.chapterItemActive}`;
  };

  /* ── collect all chapters with failed sentences ── */
  const failedSentenceSummary = useMemo(() => {
    if (chapters.length === 0) return [];
    return chapters.filter(
      (ch) =>
        (Array.isArray(ch.en_failed_sentences) && ch.en_failed_sentences.length > 0) ||
        (Array.isArray(ch.cn_failed_sentences) && ch.cn_failed_sentences.length > 0),
    );
  }, [chapters]);

  /* ── render ── */
  return (
    <div className={styles.page}>
      {/* ── Config ── */}
      <div className={styles.card}>
        <h2 className={styles.title}>
          <span className={styles.titleIcon}>☁</span>
          Azure 语音生成
        </h2>
        <p className={styles.hint}>
          使用 Azure Speech Services 批量生成语音，上传至 OSS（与 Edge TTS 输出路径兼容）
        </p>

        {/* Volume */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            选择卷
            {chaptersInVolume.length > 0 && (
              <span className={styles.labelHint}>共 {chaptersInVolume.length} 章</span>
            )}
          </label>
          <select
            className={styles.select}
            value={selectedVolumeId}
            onChange={(e) => setSelectedVolumeId(e.target.value)}
            disabled={volumesLoading || running}
          >
            {volumes.length === 0 && <option value="">暂无数据</option>}
            {volumes.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {v.novel?.title ? `${v.novel.title} — ${v.title}` : v.title || `卷 ${v.id}`}
              </option>
            ))}
          </select>
        </div>

        {/* ── English voice ── */}
        <div className={styles.voiceSection}>
          <label className={styles.label}>
            英文语音
            <span className={styles.labelHint}>
              {enVoicesFiltered.length}/{allVoices.en.length} 个语音
            </span>
          </label>
          <div className={styles.filterRow}>
            <select className={styles.filterSelect} value={enGender} onChange={(e) => setEnGender(e.target.value)} disabled={running}>
              <option value="">全部性别</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            <select className={styles.filterSelect} value={enRegion} onChange={(e) => setEnRegion(e.target.value)} disabled={running}>
              <option value="">全部地区</option>
              {enRegions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className={styles.filterSelect} value={enMulti} onChange={(e) => setEnMulti(e.target.value)} disabled={running}>
              <option value="">多语言：全部</option>
              <option value="yes">仅多语言</option>
              <option value="no">非多语言</option>
            </select>
          </div>
          <select
            className={styles.select}
            value={enVoice}
            onChange={(e) => setEnVoice(e.target.value)}
            disabled={voicesLoading || running}
          >
            {enVoicesFiltered.map((v) => (
              <option key={v.shortName} value={v.shortName}>
                {v.localName || v.displayName} ({v.shortName}) — {v.gender}{v.multilingual ? ' 🌐' : ''}
              </option>
            ))}
            {enVoicesFiltered.length === 0 && <option value="">无匹配语音</option>}
          </select>
        </div>

        {/* ── Chinese voice ── */}
        <div className={styles.voiceSection}>
          <label className={styles.label}>
            中文语音
            <span className={styles.labelHint}>
              {cnVoicesFiltered.length}/{allVoices.zh.length} 个语音
            </span>
          </label>
          <div className={styles.filterRow}>
            <select className={styles.filterSelect} value={cnGender} onChange={(e) => setCnGender(e.target.value)} disabled={running}>
              <option value="">全部性别</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            <select className={styles.filterSelect} value={cnRegion} onChange={(e) => setCnRegion(e.target.value)} disabled={running}>
              <option value="">全部地区</option>
              {cnRegions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className={styles.filterSelect} value={cnMulti} onChange={(e) => setCnMulti(e.target.value)} disabled={running}>
              <option value="">多语言：全部</option>
              <option value="yes">仅多语言</option>
              <option value="no">非多语言</option>
            </select>
          </div>
          <select
            className={styles.select}
            value={cnVoice}
            onChange={(e) => setCnVoice(e.target.value)}
            disabled={voicesLoading || running}
          >
            {cnVoicesFiltered.map((v) => (
              <option key={v.shortName} value={v.shortName}>
                {v.localName || v.displayName} ({v.shortName}) — {v.gender}{v.multilingual ? ' 🌐' : ''}
              </option>
            ))}
            {cnVoicesFiltered.length === 0 && <option value="">无匹配语音</option>}
          </select>
        </div>

        {/* Preview */}
        <div className={styles.previewCard}>
          <div className={styles.previewHeader}>
            <label className={styles.label}>语音试听</label>
            <div className={styles.previewLangTabs}>
              <button
                className={`${styles.langTab} ${previewLang === 'en' ? styles.langTabActive : ''}`}
                onClick={() => setPreviewLang('en')}
              >
                英文 {enVoice ? `(${enVoice.split('-').slice(2).join('-') || enVoice})` : ''}
              </button>
              <button
                className={`${styles.langTab} ${previewLang === 'zh' ? styles.langTabActive : ''}`}
                onClick={() => setPreviewLang('zh')}
              >
                中文 {cnVoice ? `(${cnVoice.split('-').slice(2).join('-') || cnVoice})` : ''}
              </button>
            </div>
          </div>
          <div className={styles.previewInputRow}>
            <textarea
              className={styles.previewInput}
              placeholder={previewLang === 'en' ? 'Enter text to preview...' : '输入试听文本…'}
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              maxLength={500}
              rows={2}
            />
            <button
              className={styles.previewBtn}
              onClick={handlePreview}
              disabled={previewLoading || !previewText.trim()}
            >
              {previewLoading ? (
                <span className={styles.previewSpinner}>⏳</span>
              ) : (
                <span className="material-symbols-rounded" style={{ fontSize: 20 }}>play_arrow</span>
              )}
            </button>
            {previewBlobUrl && (
              <button
                className={styles.previewBtn}
                onClick={handleDownloadPreview}
                title="下载试听音频"
              >
                <span className="material-symbols-rounded" style={{ fontSize: 20 }}>download</span>
              </button>
            )}
          </div>
          <div className={styles.previewHint}>
            最多 500 字 · 点击播放按钮试听当前选择的{previewLang === 'en' ? '英文' : '中文'}语音
            {previewFilename && <span> · 已保存为 {previewFilename}</span>}
          </div>
        </div>

        {/* Concurrency */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>句子并发数</label>
          <input
            type="number"
            className={styles.input}
            value={concurrency}
            onChange={(e) => setConcurrency(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            disabled={running}
            min={1}
            max={50}
            style={{ maxWidth: 120 }}
          />
        </div>

        {/* Chapter summary */}
        {chaptersInVolume.length > 0 && (
          <div className={styles.chapterSummary}>
            章节：{chaptersInVolume.map((c) => c.title || `#${c.id}`).join('、')}
          </div>
        )}

        {globalError && <div className={styles.errorMsg}>{globalError}</div>}

        <div className={styles.actionRow}>
          {!running ? (
            <button
              className={styles.startBtn}
              disabled={volumesLoading || voicesLoading || chaptersInVolume.length === 0}
              onClick={handleStart}
            >
              开始生成
            </button>
          ) : (
            <button className={styles.stopBtn} onClick={handleStop}>
              停止
            </button>
          )}
        </div>
      </div>

      {/* ── Progress ── */}
      {(chapters.length > 0 || running) && (
        <div className={styles.card}>
          <div className={styles.progressHeader}>
            <h3 className={styles.progressTitle}>生成进度</h3>
            <span className={styles.progressStats}>
              {succeeded + failed}/{chaptersInVolume.length} 章 | 成功 {succeeded} | 失败 {failed}
              {taskStatus && ` | ${taskStatus}`}
            </span>
          </div>

          <div className={styles.progressBarOuter}>
            <div
              className={`${styles.progressBarInner} ${
                taskStatus === 'completed' ? styles.progressBarDone : ''
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className={styles.chapterGrid}>
            {chaptersInVolume.map((vol_ch, idx) => {
              const ch = chapters.find((c) => c.chapter_id === vol_ch.id);
              const enFails = ch?.en_failed_sentences || [];
              const cnFails = ch?.cn_failed_sentences || [];
              const hasFails = enFails.length > 0 || cnFails.length > 0;
              const isExpanded = expandedChapterId === vol_ch.id;
              return (
                <div key={vol_ch.id} className={chapterItemClass(ch, idx)}>
                  <div
                    className={styles.chapterRow}
                    onClick={hasFails ? () => setExpandedChapterId(isExpanded ? null : vol_ch.id) : undefined}
                    style={hasFails ? { cursor: 'pointer' } : undefined}
                  >
                    <span className={styles.chapterIdx}>{idx + 1}</span>
                    <span className={styles.chapterName}>
                      {vol_ch.title || `#${vol_ch.id}`}
                      {hasFails && (
                        <span className={styles.failBadge}>
                          {enFails.length + cnFails.length} 句失败
                        </span>
                      )}
                    </span>
                    <div className={styles.chapterStatus}>
                      <span title="英文">{renderTag(ch?.en)}</span>
                      <span title="中文">{renderTag(ch?.cn)}</span>
                    </div>
                  </div>
                  {isExpanded && hasFails && (
                    <div className={styles.failDetail}>
                      {enFails.length > 0 && (
                        <div className={styles.failGroup}>
                          <div className={styles.failGroupTitle}>英文失败句子（章节 {vol_ch.id}）</div>
                          {enFails.map((f, fi) => (
                            <div key={fi} className={styles.failItem}>
                              <span className={styles.failSid}>句子 #{f.sentence_id}</span>
                              <span className={styles.failText}>{f.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {cnFails.length > 0 && (
                        <div className={styles.failGroup}>
                          <div className={styles.failGroupTitle}>中文失败句子（章节 {vol_ch.id}）</div>
                          {cnFails.map((f, fi) => (
                            <div key={fi} className={styles.failItem}>
                              <span className={styles.failSid}>句子 #{f.sentence_id}</span>
                              <span className={styles.failText}>{f.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Failed Sentences Summary ── */}
      {failedSentenceSummary.length > 0 && !running && (
        <div className={styles.card}>
          <h3 className={styles.failSummaryTitle}>
            失败句子汇总
            <span className={styles.failSummaryHint}>
              共 {failedSentenceSummary.length} 个章节有失败句子，请到翻译页面检查并重新翻译
            </span>
          </h3>
          <div className={styles.failSummaryList}>
            {failedSentenceSummary.map((ch) => {
              const enFails = ch.en_failed_sentences || [];
              const cnFails = ch.cn_failed_sentences || [];
              return (
                <div key={ch.chapter_id} className={styles.failSummaryChapter}>
                  <div className={styles.failSummaryChapterHeader}>
                    <span className={styles.failSummaryChapterId}>章节 #{ch.chapter_id}</span>
                    <span className={styles.failSummaryChapterTitle}>{ch.title}</span>
                    <span className={styles.failSummaryCount}>
                      {enFails.length > 0 && `英文 ${enFails.length} 句`}
                      {enFails.length > 0 && cnFails.length > 0 && ' · '}
                      {cnFails.length > 0 && `中文 ${cnFails.length} 句`}
                    </span>
                  </div>
                  {enFails.length > 0 && (
                    <table className={styles.failTable}>
                      <thead>
                        <tr>
                          <th style={{ width: 80 }}>句子 ID</th>
                          <th style={{ width: 50 }}>序号</th>
                          <th>内容（英文）</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enFails.map((f, fi) => (
                          <tr key={fi}>
                            <td className={styles.failTableId}>{f.sentence_id}</td>
                            <td>{f.index}</td>
                            <td className={styles.failTableText}>{f.text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {cnFails.length > 0 && (
                    <table className={styles.failTable}>
                      <thead>
                        <tr>
                          <th style={{ width: 80 }}>句子 ID</th>
                          <th style={{ width: 50 }}>序号</th>
                          <th>内容（中文）</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cnFails.map((f, fi) => (
                          <tr key={fi}>
                            <td className={styles.failTableId}>{f.sentence_id}</td>
                            <td>{f.index}</td>
                            <td className={styles.failTableText}>{f.text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Logs ── */}
      <div className={styles.card}>
        <div className={styles.logHeader}>
          <h3 className={styles.logTitle}>日志</h3>
          {logs.length > 0 && (
            <button className={styles.logClearBtn} onClick={() => setLogs([])}>
              清空
            </button>
          )}
        </div>
        <div className={styles.logBody}>
          {logs.length === 0 && <div style={{ color: '#ccc' }}>暂无日志</div>}
          {logs.map((entry, i) => (
            <div key={i} className={styles.logEntry}>
              <span className={styles.logTime}>{entry.time}</span>
              <span
                className={`${styles.logText} ${
                  entry.type === 'error'
                    ? styles.logError
                    : entry.type === 'success'
                    ? styles.logSuccess
                    : ''
                }`}
              >
                {entry.text}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export default AzureSpeechTool;

